import apiClient from '../../config/axiosConfig';
import { API_BASE_URL } from '../../utils/apiUtils';

// Enhanced validation functions
const validateSubteamId = (subteamId) => {
    if (!subteamId || typeof subteamId !== 'string' || subteamId.trim() === '') {
        throw new Error('Subteam ID is required and must be a valid string');
    }
    return subteamId.trim();
};

const validateTeamId = (teamId) => {
    if (!teamId || typeof teamId !== 'string' || teamId.trim() === '') {
        throw new Error('Team ID is required and must be a valid string');
    }
    return teamId.trim();
};

const validateSubteamName = (subteamName) => {
    if (!subteamName || typeof subteamName !== 'string' || subteamName.trim() === '') {
        throw new Error('Subteam name is required and must be a valid string');
    }

    if (subteamName.trim().length > 100) {
        throw new Error('Subteam name cannot exceed 100 characters');
    }

    return subteamName.trim();
};

const validateSubteamDescription = (description) => {
    if (description !== null && description !== undefined) {
        if (typeof description !== 'string') {
            throw new Error('Subteam description must be a string');
        }
        if (description.trim().length > 500) {
            throw new Error('Subteam description cannot exceed 500 characters');
        }
        return description.trim();
    }
    return '';
};

const validateUserId = (userId) => {
    if (!userId || typeof userId !== 'string' || userId.trim() === '') {
        throw new Error('User ID is required and must be a valid string');
    }
    return userId.trim();
};

const validatePage = (page) => {
    if (page !== null && page !== undefined) {
        const pageNum = parseInt(page, 10);
        if (isNaN(pageNum) || pageNum < 1) {
            throw new Error('Page must be a positive integer');
        }
        return pageNum;
    }
    return 1;
};

const validateLimit = (limit) => {
    if (limit !== null && limit !== undefined) {
        const limitNum = parseInt(limit, 10);
        if (isNaN(limitNum) || limitNum < 1 || limitNum > 100) {
            throw new Error('Limit must be a positive integer between 1 and 100');
        }
        return limitNum;
    }
    return 50;
};

// Enhanced error handler
const handleSubteamApiError = (error, operation = 'Subteam API operation') => {
    console.error(`[SubteamAPI] ${operation} Error:`, {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
        requestUrl: error.config?.url,
        requestMethod: error.config?.method
    });

    // Handle specific error cases
    if (error.response?.status === 401) {
        localStorage.removeItem('authToken');
        localStorage.removeItem('authUser');
        throw new Error('Your session has expired. Please log in again');
    }

    if (error.response?.status === 403) {
        throw new Error('You do not have permission to perform this action');
    }

    if (error.response?.status === 404) {
        throw new Error('Subteam not found or no longer available');
    }

    if (error.response?.status === 409) {
        throw new Error('Subteam already exists or conflicts with existing data');
    }

    if (error.response?.status === 422) {
        const validationErrors = error.response?.data?.errors || [];
        const errorMessage = validationErrors.length > 0
            ? `Validation errors: ${validationErrors.join(', ')}`
            : 'Invalid subteam data provided';
        throw new Error(errorMessage);
    }

    if (error.response?.status >= 500) {
        throw new Error('Server error. Please try again later.');
    }

    if (error.code === 'ERR_NETWORK') {
        throw new Error('Network error. Please check your connection and try again.');
    }

    if (error.code === 'ECONNABORTED') {
        throw new Error('Request timeout. Please try again.');
    }

    const errorMessage = error.response?.data?.message || error.message || 'An unexpected error occurred';
    throw new Error(errorMessage);
};

// Subteam CRUD Operations

// Create subteam
export const createSubteam = async (teamId, subteamData) => {
    try {
        const validTeamId = validateTeamId(teamId);
        const validName = validateSubteamName(subteamData.name);
        const validDescription = validateSubteamDescription(subteamData.description);

        console.log('[SubteamAPI] Creating subteam:', validName, 'for team:', validTeamId);

        const response = await apiClient.post(`${API_BASE_URL}/api/subteam/teams/${validTeamId}/subteams`, {
            name: validName,
            description: validDescription,
            logoUrl: subteamData.logoUrl || null,
            settings: subteamData.settings || {}
        });

        console.log('[SubteamAPI] Subteam created successfully:', response.data);
        return response.data;
    } catch (error) {
        console.error('[SubteamAPI] Error creating subteam:', error);
        handleSubteamApiError(error, 'Creating subteam');
    }
};

// Get team subteams
export const getTeamSubteams = async (teamId, options = {}) => {
    try {
        const validTeamId = validateTeamId(teamId);
        const validPage = validatePage(options.page);
        const validLimit = validateLimit(options.limit);

        const params = new URLSearchParams({
            page: validPage.toString(),
            limit: validLimit.toString()
        });

        if (options.status) {
            params.append('status', options.status);
        }

        console.log('[SubteamAPI] Getting subteams for team:', validTeamId);

        const response = await apiClient.get(`${API_BASE_URL}/api/subteam/teams/${validTeamId}/subteams?${params}`);

        console.log('[SubteamAPI] Team subteams retrieved successfully');
        return response.data;
    } catch (error) {
        console.error('[SubteamAPI] Error getting team subteams:', error);
        handleSubteamApiError(error, 'Getting team subteams');
    }
};

// Get subteam details
export const getSubteam = async (subteamId) => {
    try {
        const validSubteamId = validateSubteamId(subteamId);

        console.log('[SubteamAPI] Getting subteam details:', validSubteamId);

        const response = await apiClient.get(`${API_BASE_URL}/api/subteam/subteams/${validSubteamId}`);

        console.log('[SubteamAPI] Subteam details retrieved successfully');
        return response.data;
    } catch (error) {
        console.error('[SubteamAPI] Error getting subteam details:', error);
        handleSubteamApiError(error, 'Getting subteam details');
    }
};

// Update subteam
export const updateSubteam = async (subteamId, updateData) => {
    try {
        const validSubteamId = validateSubteamId(subteamId);

        const validUpdateData = {};
        if (updateData.name !== undefined) {
            validUpdateData.name = validateSubteamName(updateData.name);
        }
        if (updateData.description !== undefined) {
            validUpdateData.description = validateSubteamDescription(updateData.description);
        }
        if (updateData.logoUrl !== undefined) {
            // Allow null to remove logo, or validate URL if provided
            if (updateData.logoUrl !== null && updateData.logoUrl !== '') {
                // Basic URL validation - Firebase Storage URLs are valid
                const urlPattern = /^https?:\/\/.+/;
                if (!urlPattern.test(updateData.logoUrl)) {
                    throw new Error('Invalid logo URL format');
                }
            }
            validUpdateData.logoUrl = updateData.logoUrl || null;
        }
        if (updateData.settings !== undefined) {
            validUpdateData.settings = updateData.settings;
        }

        console.log('[SubteamAPI] Updating subteam:', validSubteamId);

        const response = await apiClient.put(`${API_BASE_URL}/api/subteam/subteams/${validSubteamId}`, validUpdateData);

        console.log('[SubteamAPI] Subteam updated successfully');
        return response.data;
    } catch (error) {
        console.error('[SubteamAPI] Error updating subteam:', error);
        handleSubteamApiError(error, 'Updating subteam');
    }
};

// Delete subteam
export const deleteSubteam = async (subteamId) => {
    try {
        const validSubteamId = validateSubteamId(subteamId);

        console.log('[SubteamAPI] Deleting subteam:', validSubteamId);

        const response = await apiClient.delete(`${API_BASE_URL}/api/subteam/subteams/${validSubteamId}`);

        console.log('[SubteamAPI] Subteam deleted successfully');
        return response.data;
    } catch (error) {
        console.error('[SubteamAPI] Error deleting subteam:', error);
        handleSubteamApiError(error, 'Deleting subteam');
    }
};

// Subteam Member Operations

// Add member to subteam
export const addMemberToSubteam = async (subteamId, userId) => {
    try {
        const validSubteamId = validateSubteamId(subteamId);
        const validUserId = validateUserId(userId);

        console.log('[SubteamAPI] Adding member to subteam:', validUserId, 'to subteam:', validSubteamId);

        const response = await apiClient.post(`${API_BASE_URL}/api/subteam/subteams/${validSubteamId}/members/${validUserId}`);

        console.log('[SubteamAPI] Member added to subteam successfully');
        return response.data;
    } catch (error) {
        console.error('[SubteamAPI] Error adding member to subteam:', error);
        handleSubteamApiError(error, 'Adding member to subteam');
    }
};

// Remove member from subteam
export const removeMemberFromSubteam = async (subteamId, userId) => {
    try {
        const validSubteamId = validateSubteamId(subteamId);
        const validUserId = validateUserId(userId);

        console.log('[SubteamAPI] Removing member from subteam:', validUserId, 'from subteam:', validSubteamId);

        const response = await apiClient.delete(`${API_BASE_URL}/api/subteam/subteams/${validSubteamId}/members/${validUserId}`);

        console.log('[SubteamAPI] Member removed from subteam successfully');
        return response.data;
    } catch (error) {
        console.error('[SubteamAPI] Error removing member from subteam:', error);
        handleSubteamApiError(error, 'Removing member from subteam');
    }
};

// Get subteam members
export const getSubteamMembers = async (subteamId, options = {}) => {
    try {
        const validSubteamId = validateSubteamId(subteamId);
        const validPage = validatePage(options.page);
        const validLimit = validateLimit(options.limit);

        const params = new URLSearchParams({
            page: validPage.toString(),
            limit: validLimit.toString()
        });

        if (options.status) {
            params.append('status', options.status);
        }

        console.log('[SubteamAPI] Getting subteam members:', validSubteamId);

        const response = await apiClient.get(`${API_BASE_URL}/api/subteam/subteams/${validSubteamId}/members?${params}`);

        console.log('[SubteamAPI] Subteam members retrieved successfully');
        return response.data;
    } catch (error) {
        console.error('[SubteamAPI] Error getting subteam members:', error);
        handleSubteamApiError(error, 'Getting subteam members');
    }
};

// Subteam Admin Operations

// Add subteam admin
export const addSubteamAdmin = async (subteamId, userId) => {
    try {
        const validSubteamId = validateSubteamId(subteamId);
        const validUserId = validateUserId(userId);

        console.log('[SubteamAPI] Adding subteam admin:', validUserId, 'to subteam:', validSubteamId);

        const response = await apiClient.post(`${API_BASE_URL}/api/subteam/subteams/${validSubteamId}/admins/${validUserId}`);

        console.log('[SubteamAPI] Subteam admin added successfully');
        return response.data;
    } catch (error) {
        console.error('[SubteamAPI] Error adding subteam admin:', error);
        handleSubteamApiError(error, 'Adding subteam admin');
    }
};

// Remove subteam admin
export const removeSubteamAdmin = async (subteamId, userId) => {
    try {
        const validSubteamId = validateSubteamId(subteamId);
        const validUserId = validateUserId(userId);

        console.log('[SubteamAPI] Removing subteam admin:', validUserId, 'from subteam:', validSubteamId);

        const response = await apiClient.delete(`${API_BASE_URL}/api/subteam/subteams/${validSubteamId}/admins/${validUserId}`);

        console.log('[SubteamAPI] Subteam admin removed successfully');
        return response.data;
    } catch (error) {
        console.error('[SubteamAPI] Error removing subteam admin:', error);
        handleSubteamApiError(error, 'Removing subteam admin');
    }
};